import { z } from 'zod';

// ── Full experience (server → client) ─────────────────────────────────────────
// An experience is a project / team assignment within a specific title.
// It lives under: Tenure → Title → Experience → Capability bullets.
//
// `titleId`  — the direct parent (required)
// `tenureId` — derived via title, included for frontend grouping convenience

export const ExperienceSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  titleId: z.string().uuid(),
  tenureId: z.string().uuid(),  // derived: titles.tenure_id, included for client convenience
  role: z.string().nullable(),    // functional role on this project (e.g. "Scrum Master")
  project: z.string().nullable(),
  team: z.string().nullable(),
  sortOrder: z.number().int(),    // user-defined display order within a title
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// ── Create request (client → server) ─────────────────────────────────────────

export const CreateExperienceSchema = z.object({
  titleId: z.string().uuid('Must be a valid title ID'),
  role: z.string().max(200).trim().optional(),
  project: z.string().max(200).trim().optional(),
  team: z.string().max(200).trim().optional(),
});

// ── Update request — descriptive fields only; titleId cannot change ──────────

export const UpdateExperienceSchema = z.object({
  role: z.string().max(200).trim().nullable().optional(),
  project: z.string().max(200).trim().nullable().optional(),
  team: z.string().max(200).trim().nullable().optional(),
});

// ── Reorder request ───────────────────────────────────────────────────────────
// Allows reordering and optionally moving experiences to different titles within the same tenure.

export const ReorderExperienceItemSchema = z.object({
  id: z.string().uuid(),
  sortOrder: z.number().int(),
  titleId: z.string().uuid().optional(),  // optional; if provided, moves experience to this title
});

export const ReorderExperiencesSchema = z.object({
  reorders: z.array(ReorderExperienceItemSchema).min(1),
});

// ── Derived types ─────────────────────────────────────────────────────────────

export type Experience = z.infer<typeof ExperienceSchema>;
export type CreateExperience = z.infer<typeof CreateExperienceSchema>;
export type UpdateExperience = z.infer<typeof UpdateExperienceSchema>;
export type ReorderExperienceItem = z.infer<typeof ReorderExperienceItemSchema>;
export type ReorderExperiences = z.infer<typeof ReorderExperiencesSchema>;
